/* ── Orbit Ring ────────────────────────────────────────────────────
 * Spinning disc-shaped ring at the swarm centroid, signaling the
 * activation of an orbit-style card (HorizontalOrbit, DiagonalOrbit,
 * generic Orbit). Distinct from HealerBeaconRing in that it spins
 * (instead of expanding outward) and is tinted blue/purple instead
 * of green.
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, uv, vec2, vec3, vec4, float, length, smoothstep } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 1.2;
const RING_RADIUS = 7;
const SPIN_RATE = 4; // rad/s
const DEFAULT_COLOR = 0xa78bff;

export type OrbitTiltMode = 'horizontal' | 'diagonal' | 'vertical';

const TILT_X_ROTATION: Record<OrbitTiltMode, number> = {
  horizontal: -Math.PI / 2,
  diagonal: -Math.PI / 4,
  vertical: 0,
};

interface OrbitRingOptions {
  color?: number;
  tilt?: OrbitTiltMode;
  liveResolver?: LivePositionResolver;
}

export class OrbitRing extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly group: THREE.Group;
  private readonly geometry: THREE.RingGeometry;
  private readonly material: THREE.MeshBasicNodeMaterial;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly liveResolver?: LivePositionResolver;

  constructor(scene: THREE.Scene, position: Vec3Like, options?: OrbitRingOptions) {
    super(scene, LIFE_SECONDS);
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);
    this.liveResolver = options?.liveResolver;
    const tilt = options?.tilt ?? 'horizontal';
    const color = options?.color ?? DEFAULT_COLOR;

    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(color));
    const uProgress = this.uProgress;

    this.geometry = new THREE.RingGeometry(RING_RADIUS * 0.85, RING_RADIUS, 64, 1);
    this.material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const colorNode = Fn(() => {
      const u = uv();
      const radial = length(u.sub(vec2(0.5, 0.5))).mul(2.0);
      const edge = smoothstep(float(0.85), float(1.0), radial).mul(
        smoothstep(float(1.0), float(0.85), radial).oneMinus(),
      );
      const rgb = vec3(uColor).add(vec3(0.2, 0.2, 0.5).mul(edge));
      const fade = float(uProgress).oneMinus();
      const alpha = fade.mul(0.75);
      return vec4(rgb, alpha);
    })();
    this.material.colorNode = colorNode;
    setEmissiveNode(this.material, colorNode);

    const ringMesh = new THREE.Mesh(this.geometry, this.material);
    ringMesh.rotation.x = TILT_X_ROTATION[tilt];
    this.group.add(ringMesh);
    this.scene.add(this.group);
  }

  protected tick(t: number, dtSec: number): void {
    this.uProgress.value = t;
    this.applyLiveAnchor(this.group, this.liveResolver);
    this.group.rotation.y += SPIN_RATE * dtSec;
  }

  protected disposeResources(): void {
    this.scene.remove(this.group);
    this.geometry.dispose();
    this.material.dispose();
  }
}
